import React from 'react';
import { History, Calendar, Images, Download, Trash2, TrendingDown, FileText, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCompressionHistory } from '@/hooks/useCompressionHistory';
import { formatBytes } from '@/utils/fileValidation';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const CompressionHistory: React.FC = () => {
  const { history, loading, deleteHistoryEntry, getTotalStats } = useCompressionHistory();
  const stats = getTotalStats();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
          <History className="w-8 h-8 text-primary" />
          Historial de Compresión
        </h1>
        <p className="text-muted-foreground">
          Revisa todas tus compresiones anteriores y estadísticas
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Compresiones</p>
                <p className="text-2xl font-bold">{stats.totalCompressions}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Imágenes Procesadas</p>
                <p className="text-2xl font-bold">{stats.totalImages.toLocaleString()}</p>
              </div>
              <Images className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Espacio Ahorrado</p>
                <p className="text-2xl font-bold">{formatBytes(stats.totalSaved)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compresión Promedio</p>
                <p className="text-2xl font-bold">{(100 - stats.averageCompression).toFixed(1)}%</p>
              </div>
              <Download className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History List */}
      <Card>
        <CardHeader>
          <CardTitle>Historial Detallado</CardTitle>
          <CardDescription>
            {history.length === 0 
              ? "No hay compresiones registradas aún" 
              : `${history.length} compresión(es) registrada(s)`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aún no has comprimido ninguna imagen.</p>
              <p>¡Empieza a comprimir para ver tu historial aquí!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div key={entry.id} className="border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {format(new Date(entry.created_at), "PPP 'a las' p", { locale: es })}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Imágenes</p>
                          <p className="font-medium">{entry.total_images}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Tamaño Original</p>
                          <p className="font-medium">{formatBytes(entry.original_size_bytes)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Tamaño Final</p>
                          <p className="font-medium">{formatBytes(entry.compressed_size_bytes)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Ahorro</p>
                          <p className="font-medium text-green-600">
                            {(100 - entry.compression_ratio).toFixed(1)}%
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary">
                          <Settings className="w-3 h-3 mr-1" />
                          {entry.preset_used}
                        </Badge>
                        <Badge variant="outline">
                          Calidad: {Math.round(entry.quality_setting * 100)}%
                        </Badge>
                        {entry.auto_resize_enabled && (
                          <Badge variant="outline">
                            Redimensión: {entry.max_width || 'Auto'} x {entry.max_height || 'Auto'}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteHistoryEntry(entry.id)}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CompressionHistory;